// ============================================================================
// LA PUERTA AMABLE. Qué ve quien entra con su cuenta buena pero todavía no
// tiene acceso a este producto.
//
// ANTES ERA UNA LÍNEA GRIS: «Tu cuenta no está asignada a ningún negocio».
// Cierto y correcto, y aun así el peor momento posible para ser escueto: es la
// PRIMERA pantalla de alguien que acaba de entrar por primera vez. Un asesor
// que llega el lunes a las 8 de la mañana y ve un renglón gris concluye que
// «esto no sirve» — y esa conclusión no se corrige después.
//
// Así que esta pantalla hace tres cosas, en este orden:
//   1. **Le dice que no hizo nada mal.** Su cuenta está bien; falta el permiso,
//      y el permiso lo da otra persona. Sin jerga y sin cara de error.
//   2. **Le dice exactamente qué pedir y a quién.** Una instrucción que se
//      puede reenviar por WhatsApp tal cual.
//   3. **Le enseña qué más hay.** No por vender: porque quien ve el tamaño de
//      lo que hay entiende dónde está parado, y le dan ganas de entrar.
//
// ⚠️⚠️ REGLA QUE NO SE NEGOCIA: AQUÍ SOLO VAN LOS PRODUCTOS DE VICTORIX.
// Esta pantalla la ve **gente de un cliente** (hoy, los asesores de Living
// House). El catálogo de `plataforma.productos` incluye además el Balance de
// IVA, el Portal Renta, RRHH y Koalas — que son **de la firma SIA y de sus
// empresas**. Ponerlos aquí le enseñaría a un cliente qué le construimos a otro:
// no es un descuido de copy, es filtrar la cartera. Van SOLO los tres que se
// venden a cualquiera: Chat AI, Work OS y el ERP.
//
// ⚠️ Y POR ESO MISMO EL CATÁLOGO NO SE LEE DE LA BASE. `plataforma.productos`
// tiene RLS forzada y CERO políticas a propósito: nadie la lee desde el
// navegador. Y aunque se abriera, esta pantalla es justo la de quien **no tiene
// ningún permiso**: cualquier consulta le devolvería vacío. Además, leerla de
// ahí traería automáticamente los productos de SIA — el error de arriba, pero
// sin que nadie lo escriba. Se mantiene a mano, y son tres filas.
// ============================================================================
import { LogoVXAI } from "@victorix/chat-ai-ui";
import { useSesion } from "../lib/auth.tsx";

interface Producto {
  nombre: string;
  que: string;
  /** El producto al que la persona intentaba entrar: se marca, no se esconde. */
  esEste?: boolean;
}

// ⚠️ El agente de IA del ERP **todavía no existe** (agosto 2026). Va anunciado
// por decisión explícita de Cristhian. Lo que sí está vivo de esa fila es la
// conexión con la tienda en línea, las fotos y las descripciones por IA. Si
// alguien pregunta por el agente antes de que exista, la respuesta honesta es
// «viene en camino», no improvisar una demostración.
const PRODUCTOS: Producto[] = [
  {
    nombre: "Chat AI",
    que: "Un agente de IA que atiende WhatsApp y no deja caer un cliente, con CRM incluido: cada conversación queda con su etapa y su seguimiento.",
    esEste: true,
  },
  {
    nombre: "Work OS",
    que: "Tareas, proyectos y equipo. Con un agente de WhatsApp al que le dictas: crea la tarea, te la recuerda y te organiza el tablero.",
  },
  {
    nombre: "ERP",
    que: "Inventario, costos, producción y factura electrónica. Con agente de IA y conexión directa a tu tienda en línea: genera las fotos y escribe las descripciones.",
  },
];

/** La marca del producto al que la persona intentaba entrar. */
function Marca({ className = "" }: { className?: string }) {
  return (
    <span
      className={
        "shrink-0 rounded-full bg-marca-suave px-2.5 py-1 text-[11px] font-medium text-marca " +
        className
      }
    >
      aquí ibas
    </span>
  );
}

export default function SinAcceso() {
  const { correo, salir } = useSesion();

  return (
    <div className="min-h-pantalla bg-papel text-tinta">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
        <LogoVXAI className="h-7 w-auto" />

        {/* El mensaje. Jerarquía por tipografía y aire, no por cajas ni alertas:
            esto NO es un error, y no debe parecerlo. */}
        <h1 className="mt-12 text-[28px] sm:text-[34px] font-semibold tracking-[-0.02em] leading-[1.15]">
          Todavía no tienes acceso a Chat AI
        </h1>
        <p className="mt-4 text-[15px] sm:text-base leading-relaxed text-tinta-2 max-w-xl">
          Tu cuenta está bien y la sesión quedó iniciada. Falta que quien administra Chat AI en tu
          empresa te invite desde <span className="text-tinta">Equipo</span> — toma un minuto, y no
          tienes que volver a crear nada.
        </p>

        {correo && (
          <p className="mt-6 text-[13px] text-tinta-2">
            Entraste como <span className="text-tinta font-medium">{correo}</span>.{" "}
            <button
              onClick={() => void salir()}
              className="underline underline-offset-4 decoration-borde hover:decoration-tinta-2 transition"
            >
              ¿No eres tú? Cerrar sesión
            </button>
          </p>
        )}

        {/* El catálogo. Filas con filete, sin tarjetas: el sistema de la casa
            pide jerarquía por espacio. La que la persona buscaba va marcada. */}
        <div className="mt-16">
          <h2 className="text-[15px] sm:text-base font-medium text-tinta">
            En Victorix desarrollamos automatizaciones con IA para empresas e instituciones
          </h2>
          {/* ⚠️ En móvil NO son dos columnas. Con el nombre a la izquierda y la
              marca «aquí ibas» a la derecha, a 375 px la descripción se queda
              con 100 px y cae en cinco renglones rotos. Se apila: nombre (con su
              marca al lado) y debajo la frase. Las dos columnas vuelven en `sm`.
              ⚠️ La marca se dibuja UNA sola vez y se reordena con `order`, no se
              duplica con `sm:hidden`/`hidden sm:block`: dos copias en el DOM se
              las lee DOS VECES un lector de pantalla, y ninguna regla de CSS
              silencia eso. En móvil va pegada al nombre (order 1) y la frase se
              va a su propio renglón (`w-full`); desde `sm` la frase se mete en
              medio (order 1) y la marca se empuja al extremo (order 2). */}
          <ul className="mt-5 border-t border-borde-suave">
            {PRODUCTOS.map((p) => (
              <li
                key={p.nombre}
                className="flex flex-wrap items-center gap-x-2 border-b border-borde-suave py-4 sm:flex-nowrap sm:items-baseline sm:gap-x-4 sm:py-5"
              >
                <span
                  className={
                    "text-[15px] sm:text-base font-medium sm:w-44 sm:shrink-0 " +
                    (p.esEste ? "text-marca" : "text-tinta")
                  }
                >
                  {p.nombre}
                </span>
                {/* `self-baseline`, no `self-center`: con una descripción de tres
                    renglones, centrado deja la marca flotando a media fila. Va
                    pegada al primer renglón, que es donde está el nombre. */}
                {p.esEste && <Marca className="order-1 sm:order-2 sm:ml-auto sm:self-baseline" />}
                <p className="order-2 mt-1 w-full text-[14px] leading-relaxed text-tinta-2 sm:order-1 sm:mt-0 sm:w-auto sm:text-[15px]">
                  {p.que}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* El cierre: la idea que hace que los tres valgan más juntos que
            sueltos. Va después de la lista, no antes, porque solo se entiende
            cuando ya se sabe qué es cada uno. */}
        <p className="mt-10 text-[15px] sm:text-base leading-relaxed text-tinta max-w-2xl">
          Y los tres se conectan entre sí: el mismo cliente, el mismo catálogo y el mismo equipo,
          sin copiar datos de un lado a otro.{" "}
          <span className="text-tinta-2">
            Un ecosistema para empresas y emprendedores, para que el trabajo sea simple y las ventas
            lleguen más fácil.
          </span>
        </p>

        <p className="mt-10 text-[13px] leading-relaxed text-tinta-2">
          ¿Quieres alguno de estos para tu empresa? Háblalo con quien te trajo hasta acá.
        </p>
      </div>
    </div>
  );
}
